/**
 * Audio-reactive orb. Layered organic blobs in the Timo palette, driven by the live microphone level
 * while recording and by a gentle pulse while voice input is listening. Canvas 2D, no dependencies.
 */
export type OrbState = "off" | "idle" | "listening" | "recording" | "paused" | "processing";

export interface Orb {
  setLevel(rms: number): void;
  setState(state: OrbState): void;
  destroy(): void;
}

const PALETTE = { forest: [31, 92, 72], mint: [226, 239, 231], spark: [216, 240, 111], deep: [23, 35, 31], blue: [106, 159, 194] } as const;
const rgba = (c: readonly number[], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;

export function createOrb(host: HTMLElement, size = 160): Orb {
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const canvas = document.createElement("canvas");
  canvas.width = size * dpr; canvas.height = size * dpr;
  canvas.style.width = `${size}px`; canvas.style.height = `${size}px`;
  canvas.className = "timo-orb";
  host.appendChild(canvas);
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);

  let level = 0, smooth = 0, state: OrbState = "idle", raf = 0, t = 0;
  const seeds = Array.from({ length: 3 }, (_, i) => ({ speed: 0.6 + i * 0.35, phase: i * 2.1, lobes: 3 + i }));

  const blob = (cx: number, cy: number, r: number, wobble: number, seed: { speed: number; phase: number; lobes: number }) => {
    ctx.beginPath();
    for (let a = 0; a <= Math.PI * 2 + 0.01; a += Math.PI / 36) {
      const w = 1 + wobble * (Math.sin(a * seed.lobes + t * seed.speed + seed.phase) * 0.5 + Math.sin(a * (seed.lobes + 2) - t * seed.speed * 1.3) * 0.25);
      const x = cx + Math.cos(a) * r * w, y = cy + Math.sin(a) * r * w;
      a === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath();
  };

  const frame = () => {
    raf = requestAnimationFrame(frame);
    t += reduce ? 0 : 0.016;
    smooth += ((state === "recording" ? Math.min(1, level * 6) : state === "listening" ? 0.35 + Math.sin(t * 3) * 0.25 : state === "processing" ? 0.25 + Math.sin(t * 5) * 0.2 : 0.08) - smooth) * 0.12;
    const c = size / 2;
    const base = size * 0.28 * (1 + smooth * 0.35);
    ctx.clearRect(0, 0, size, size);
    if (state === "off") return;

    // Glow
    const glow = ctx.createRadialGradient(c, c, base * 0.4, c, c, base * 1.9);
    glow.addColorStop(0, rgba(state === "paused" ? PALETTE.blue : PALETTE.spark, 0.28 + smooth * 0.3));
    glow.addColorStop(1, rgba(PALETTE.mint, 0));
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, size, size);

    // Three overlapping lobes: forest core, mint mid, spark highlight
    const wobble = reduce ? 0.02 : 0.08 + smooth * 0.22;
    ctx.globalCompositeOperation = "source-over";
    blob(c, c, base * 1.12, wobble, seeds[2]!);
    ctx.fillStyle = rgba(state === "paused" ? PALETTE.blue : PALETTE.forest, 0.22 + smooth * 0.15);
    ctx.fill();
    blob(c + Math.sin(t * 0.9) * 3, c + Math.cos(t * 0.7) * 3, base * 0.98, wobble * 1.15, seeds[1]!);
    const g2 = ctx.createRadialGradient(c - base * 0.3, c - base * 0.3, 0, c, c, base);
    g2.addColorStop(0, rgba(PALETTE.mint, 0.95));
    g2.addColorStop(0.6, rgba(state === "paused" ? PALETTE.blue : PALETTE.forest, 0.85));
    g2.addColorStop(1, rgba(PALETTE.deep, 0.9));
    ctx.fillStyle = g2;
    ctx.fill();
    blob(c + Math.cos(t * 1.1) * 4, c - Math.sin(t * 0.8) * 4, base * 0.55 * (1 + smooth * 0.5), wobble * 1.6, seeds[0]!);
    ctx.fillStyle = rgba(PALETTE.spark, 0.55 + smooth * 0.4);
    ctx.globalCompositeOperation = "lighter";
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
  };
  frame();

  return {
    setLevel: (rms) => { level = rms; },
    setState: (s) => { state = s; canvas.dataset.state = s; },
    destroy: () => { cancelAnimationFrame(raf); canvas.remove(); },
  };
}
